import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowLeft';
import { TypedMemo } from '../../../common/types';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../common/views/LoadingView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { kitPictureSrc } from '../kitPictureSrc';
import { useArchivedPosts } from './hooks/useArchivedPosts';
import { useArchivePostFiles } from './hooks/useArchivePostFiles';
import { archiveOutcomeText, numbersText, postFileLabel, postFileThumbPicture, PostsBoardFile } from './postsModel';
import { postsMessages } from './postsMessages';
import { PostsArchiveNotice } from './PostsArchiveNotice';
import { PostsFileViewer } from './PostsFileViewer';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Posts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostsArchiveViewProps {
  accountId: number;
  catalogItemUuid: string | undefined;
  outcome: 'posted' | 'never' | undefined;
  onOutcome: (outcome: 'posted' | 'never' | undefined) => void;
  onBack: () => void;
  onOpen: (file: PostsBoardFile) => void;
  /** The file open in the viewer (`?asset=`). */
  openAsset: string | undefined;
  onCloseAsset: () => void;
}

/**
 * The archive (design assets-rethink c2): every file the user archived, kept forever. Restore puts a
 * file back on the board (decision 4). Tabs All, Posted, Never posted; a row opens the viewer.
 */
const PostsArchiveViewComponent: React.FC<PostsArchiveViewProps> = (props) => {
  const { accountId, catalogItemUuid, outcome, onOutcome, onBack, onOpen, openAsset, onCloseAsset } = props;
  const intl = useIntl();
  const { archive, isLoading, error, refetch } = useArchivedPosts({ accountId, catalogItemUuid, outcome });
  const restore = useArchivePostFiles(accountId);
  const files = useMemo(() => archive?.items.map((row) => row.file) ?? [], [archive]);
  const [restoring, setRestoring] = useState<string | null>(null);
  const { run } = restore;
  const restoreOne = useCallback(
    (file: PostsBoardFile) => {
      setRestoring(file.key);
      run([file], false)
        .catch(console.error)
        .finally(() => setRestoring(null));
    },
    [run]
  );
  const shortDate = (iso: string | null): string =>
    iso === null ? '—' : intl.formatDate(new Date(iso), { day: 'numeric', month: 'short' });

  const tabs: Array<{ value: 'posted' | 'never' | undefined; label: string }> = [
    { value: undefined, label: intl.formatMessage(postsMessages.archiveAll, { count: archive?.counts.all ?? 0 }) },
    { value: 'posted', label: intl.formatMessage(postsMessages.archivePosted, { count: archive?.counts.posted ?? 0 }) },
    { value: 'never', label: intl.formatMessage(postsMessages.archiveNever, { count: archive?.counts.never ?? 0 }) },
  ];

  return (
    <div data-testid="posts-archive">
      <div className={styles.toolbar}>
        <button type="button" className={styles.link} onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          {intl.formatMessage(postsMessages.backToPosts)}
        </button>
        <span className={styles.grow} />
        <div className={styles.chips} role="group">
          {tabs.map((tab) => (
            <button
              key={tab.value ?? 'all'}
              type="button"
              aria-pressed={tab.value === outcome}
              className={clsx(styles.chip, tab.value === outcome && styles.chipOn)}
              onClick={() => onOutcome(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-muted small">{intl.formatMessage(postsMessages.archiveSubtitle)}</p>
      <PostsArchiveNotice blocked={[]} failed={restore.failed} onOpenPost={() => undefined} onClose={restore.clear} />
      {isLoading && <LoadingView />}
      {error !== undefined && <ErrorView error={error as Error} retry={refetch} />}
      {archive !== undefined && archive.items.length === 0 && (
        <div className={styles.laneEmpty}>{intl.formatMessage(postsMessages.archiveEmpty)}</div>
      )}
      {archive !== undefined && archive.items.length > 0 && (
        <table className={styles.archiveTable}>
          <thead>
            <tr>
              <th>{intl.formatMessage(postsMessages.columnFile)}</th>
              <th>{intl.formatMessage(postsMessages.columnOutcome)}</th>
              <th>{intl.formatMessage(postsMessages.columnArchived)}</th>
              <th>{intl.formatMessage(postsMessages.columnNumbers)}</th>
              <th aria-label={intl.formatMessage(postsMessages.restore)} />
            </tr>
          </thead>
          <tbody>
            {archive.items.map((row) => {
              const thumb = postFileThumbPicture(row.file);
              const song = row.file.catalogItemUuid === null ? undefined : archive.songs[row.file.catalogItemUuid];
              return (
                <tr key={row.file.key} data-testid="posts-archive-row">
                  <td>
                    <button type="button" className={styles.archiveFile} onClick={() => onOpen(row.file)}>
                      {thumb === null ? (
                        <span className={clsx(styles.thumb, styles.thumbSmall)} />
                      ) : (
                        <img className={clsx(styles.thumb, styles.thumbSmall)} src={kitPictureSrc(thumb, 32)} alt="" />
                      )}
                      <span>
                        {postFileLabel(intl, row.file)}
                        <small>{song?.title ?? intl.formatMessage(postsMessages.noSong)}</small>
                      </span>
                    </button>
                  </td>
                  <td>{archiveOutcomeText(intl, row)}</td>
                  <td>{shortDate(row.file.archivedAt)}</td>
                  <td>{numbersText(intl, row.views, row.likes) ?? '—'}</td>
                  <td className="text-end">
                    {!archive.readOnly && (
                      <SMLButton
                        color="secondary"
                        outline
                        size="sm"
                        omitCap
                        isLoading={restoring === row.file.key}
                        onClick={() => restoreOne(row.file)}
                        testID="posts-restore"
                      >
                        {intl.formatMessage(postsMessages.restore)}
                      </SMLButton>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <PostsFileViewer accountId={accountId} assetUuid={openAsset} files={files} onClose={onCloseAsset} />
    </div>
  );
};

export const PostsArchiveView = TypedMemo(PostsArchiveViewComponent);
